import { Measure, useMeasurementPresentation } from '../Measurement'
import { useState, useMemo, useRef, useEffect } from 'react'
import { RotateCcw, ZoomIn, ZoomOut, Crosshair, Box } from 'lucide-react'
import { useTheme } from '../../context/ThemeContext'
import { convertMeasurement, parseMeasurementUnit } from '../../lib/measurement'
import { resolveProductCenterOfGravity } from '../../lib/productCenterOfGravity'

interface ProductDimensions3DPreviewProps {
    length: number | null
    width: number | null
    height: number | null
    weight?: number | null
    unit?: string
    weightUnit?: string
    color?: string
    calculatedVolume?: number | null
    /** Coordonnées locales du centre de gravité, en mm depuis le coin inférieur. */
    centerOfGravity?: { x?: number | null; y?: number | null; z?: number | null }
    appearance?: 'theme' | 'light'
}

// ── Helper to adjust color brightness ────────────────────────────────────────
function adjustBrightness(hexColor: string, factor: number): string {
    let hex = (hexColor || '#6366f1').replace('#', '')
    if (hex.length === 3) hex = hex.split('').map(c => c + c).join('')
    const r = parseInt(hex.substring(0, 2), 16) || 99
    const g = parseInt(hex.substring(2, 4), 16) || 102
    const b = parseInt(hex.substring(4, 6), 16) || 241

    const nr = Math.min(255, Math.max(0, Math.round(r * factor)))
    const ng = Math.min(255, Math.max(0, Math.round(g * factor)))
    const nb = Math.min(255, Math.max(0, Math.round(b * factor)))

    return `#${nr.toString(16).padStart(2, '0')}${ng.toString(16).padStart(2, '0')}${nb.toString(16).padStart(2, '0')}`
}

export default function ProductDimensions3DPreview({
    length,
    width,
    height,
    unit = 'cm',
    color = '#6366f1',
    calculatedVolume,
    centerOfGravity,
    appearance = 'theme',
}: ProductDimensions3DPreviewProps) {
    const { theme } = useTheme()
    const presentation = useMeasurementPresentation()
    const isDark = appearance !== 'light' && theme === 'dark'

    // Use real dimensions or sensible default fallbacks.
    // Normalize strings as numbers too, because props can sometimes come from form state
    // or API payloads as numeric strings. Otherwise `h + zAxisExt` may concatenate
    // and produce a huge invalid Z coordinate.
    const normalizedLength = Number(length)
    const normalizedWidth = Number(width)
    const normalizedHeight = Number(height)
    const l = Number.isFinite(normalizedLength) && normalizedLength > 0 ? normalizedLength : 120
    const w = Number.isFinite(normalizedWidth) && normalizedWidth > 0 ? normalizedWidth : 80
    const h = Number.isFinite(normalizedHeight) && normalizedHeight > 0 ? normalizedHeight : 100
    const cg = resolveProductCenterOfGravity({ length, width, height, dimensionUnit: unit, ...centerOfGravity })
    const cgLocal = cg.pointMm ? {
        x: convertMeasurement(cg.pointMm.x, 'mm', parseMeasurementUnit(unit)),
        y: convertMeasurement(cg.pointMm.y, 'mm', parseMeasurementUnit(unit)),
        z: convertMeasurement(cg.pointMm.z, 'mm', parseMeasurementUnit(unit)),
    } : null
    const cgCoordinates = cg.pointMm ? `X ${presentation.format(cg.pointMm.x, 'mm', 'cargoLength')} · Y ${presentation.format(cg.pointMm.y, 'mm', 'cargoLength')} · Z ${presentation.format(cg.pointMm.z, 'mm', 'cargoLength')}` : ''

    // Container styling based on theme
    const containerClass = isDark
        ? 'rounded-2xl border border-slate-700/80 bg-slate-900/90 shadow-sm overflow-hidden'
        : 'rounded-2xl border border-slate-200/80 bg-white shadow-sm overflow-hidden'
    const resetButtonClass = isDark
        ? 'inline-flex min-h-10 min-w-10 items-center justify-center p-2 rounded-lg bg-slate-800 text-slate-400 hover:text-white border border-slate-700 transition-colors disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange-500'
        : 'inline-flex min-h-10 min-w-10 items-center justify-center p-2 rounded-lg bg-white text-slate-500 hover:bg-orange-50 hover:text-orange-600 border border-slate-200 transition-colors disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange-500'
    const canvasContainerClass = isDark
        ? 'relative h-80 bg-slate-950/60 flex items-center justify-center cursor-grab active:cursor-grabbing select-none overflow-hidden'
        : 'relative h-80 bg-slate-50/60 flex items-center justify-center cursor-grab active:cursor-grabbing select-none overflow-hidden'
    const hintTextColor = isDark ? 'text-slate-400' : 'text-slate-500'
    const gridStroke = isDark ? 'rgba(148, 163, 184, 0.15)' : 'rgba(148, 163, 184, 0.2)'
    const gridLineStroke = isDark ? '#64748b' : '#94a3b8'
    const xAxisColor = isDark ? '#fb923c' : '#ea580c'
    const faceStroke = isDark ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.25)'

    // ── Camera Orbit Interaction State ──────────────────────────────────────────
    const [yaw, setYaw] = useState<number>(225) // Yaw angle around Z
    const [pitch, setPitch] = useState<number>(28) // Pitch angle (tilt)
    const [zoom, setZoom] = useState(1)
    const [pan, setPan] = useState({ x: 0, y: 0 })
    const [selectedFace, setSelectedFace] = useState<string | null>(null)
    const [autoRotate, setAutoRotate] = useState<boolean>(false)
    const viewportRef = useRef<HTMLDivElement>(null)
    const svgRef = useRef<SVGSVGElement>(null)
    const dragCleanupRef = useRef<(() => void) | null>(null)
    const animRef = useRef<number | null>(null)
    useEffect(() => () => dragCleanupRef.current?.(), [])
    useEffect(() => {
        const viewport = viewportRef.current
        if (!viewport) return
        const handleWheel = (event: WheelEvent) => {
            // Preserve browser zoom shortcuts and horizontal scrolling.
            if (event.ctrlKey || event.metaKey || !Number.isFinite(event.deltaY) || event.deltaY === 0) return
            event.preventDefault()
            const pixels = event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? viewport.clientHeight : 1)
            const delta = Math.max(-100, Math.min(100, pixels))
            setZoom(value => Math.max(0.5, Math.min(2.5, value * Math.exp(-delta * 0.002))))
        }
        // React wheel listeners are passive; use a local active listener to stop form scrolling.
        viewport.addEventListener('wheel', handleWheel, { passive: false })
        return () => viewport.removeEventListener('wheel', handleWheel)
    }, [])

    // Button class that depends on autoRotate state
    const autoRotateButtonClass = autoRotate
        ? isDark
            ? 'bg-orange-500/20 text-orange-400 border-orange-500/40'
            : 'bg-orange-50 text-orange-600 border-orange-200'
        : isDark
            ? 'bg-slate-800 text-slate-400 border-slate-700 hover:text-white'
            : 'bg-white text-slate-500 border-slate-200 hover:bg-orange-50 hover:text-orange-600'

    // Auto-rotation effect
    useEffect(() => {
        if (autoRotate) {
            const step = () => {
                setYaw(prev => (prev + 0.6) % 360)
                animRef.current = requestAnimationFrame(step)
            }
            animRef.current = requestAnimationFrame(step)
        } else {
            if (animRef.current) cancelAnimationFrame(animRef.current)
        }
        return () => {
            if (animRef.current) cancelAnimationFrame(animRef.current)
        }
    }, [autoRotate])

    // Left drag orbits; right drag translates in screen directions at any zoom.
    const handlePointerDown = (e: React.PointerEvent) => {
        if (e.button !== 0 && e.button !== 2) return
        const translate = e.button === 2
        if (translate) e.preventDefault()
        dragCleanupRef.current?.()
        setAutoRotate(false)
        const pointerId = e.pointerId
        const startX = e.clientX
        const startY = e.clientY
        const startYaw = yaw
        const startPitch = pitch
        const startPan = pan
        const unitsPerPixel = 1 / (svgRef.current?.getScreenCTM()?.a || 1)

        const handlePointerMove = (moveEvent: PointerEvent) => {
            if (moveEvent.pointerId !== pointerId) return
            const dx = moveEvent.clientX - startX
            const dy = moveEvent.clientY - startY
            if (translate) setPan({ x: startPan.x + dx * unitsPerPixel, y: startPan.y + dy * unitsPerPixel })
            else {
                setYaw((startYaw - dx * 0.6 + 360) % 360)
                setPitch(Math.max(10, Math.min(75, startPitch + dy * 0.5)))
            }
        }

        const cleanup = () => {
            window.removeEventListener('pointermove', handlePointerMove)
            window.removeEventListener('pointerup', handlePointerUp)
            window.removeEventListener('pointercancel', handlePointerUp)
            window.removeEventListener('blur', cleanup)
            dragCleanupRef.current = null
        }
        const handlePointerUp = (event: PointerEvent) => {
            if (event.pointerId === pointerId) cleanup()
        }
        dragCleanupRef.current = cleanup

        window.addEventListener('pointermove', handlePointerMove)
        window.addEventListener('pointerup', handlePointerUp)
        window.addEventListener('pointercancel', handlePointerUp)
        window.addEventListener('blur', cleanup)
    }

    // ── 3D Projection Engine ───────────────────────────────────────────────────
    const svgWidth = 460
    const svgHeight = 240
    const cx = svgWidth / 2
    const cy = svgHeight / 2 + 15

    const maxDim = Math.max(l, w, h) || 100
    const scale = (Math.min(svgWidth, svgHeight) / maxDim) * 0.42

    const radYaw = (yaw * Math.PI) / 180
    const radPitch = (pitch * Math.PI) / 180

    // Project (x, y, z) centered at box origin to 2D SVG canvas
    const project = (x: number, y: number, z: number): [number, number, number] => {
        const x1 = x * Math.cos(radYaw) - y * Math.sin(radYaw)
        const y1 = x * Math.sin(radYaw) + y * Math.cos(radYaw)
        const z1 = z

        const sx = cx + x1 * scale
        const sy = cy - (z1 * Math.cos(radPitch) - y1 * Math.sin(radPitch)) * scale
        const depth = y1 * Math.cos(radPitch) + z1 * Math.sin(radPitch)

        return [sx, sy, depth]
    }

    // 8 vertices centered around base floor (z from 0 to h)
    const halfL = l / 2
    const halfW = w / 2

    const vertices: [number, number, number][] = [
        [halfL, halfW, 0],   // 0: Origin Corner (Xmax, Ymax, 0) - Top Right (will be displayed as top-left visually)
        [-halfL, halfW, 0],  // 1: Xmin, Ymax, 0 - Top Left
        [-halfL, -halfW, 0], // 2: Xmin, Ymin, 0 - Bottom Left
        [halfL, -halfW, 0],  // 3: Xmax, Ymin, 0 - Bottom Right
        [halfL, halfW, h],   // 4: Xmax, Ymax, H - Top Right
        [-halfL, halfW, h],  // 5: Xmin, Ymax, H - Top Left
        [-halfL, -halfW, h], // 6: Xmin, Ymin, H - Bottom Left
        [halfL, -halfW, h],  // 7: Xmax, Ymin, H - Bottom Right
    ]

    const pVerts = useMemo(() => vertices.map(v => project(v[0], v[1], v[2])), [l, w, h, yaw, pitch, scale, cx, cy])

    // Faces definitions
    const faces = [
        { id: 'floor', name: 'Surface au Sol (Base)', indices: [0, 1, 2, 3], shade: 0.55, dimText: `${presentation.format(l, unit, 'cargoLength', false)} × ${presentation.format(w, unit, 'cargoLength', false)} ${presentation.unit('cargoLength')}` },
        { id: 'top', name: 'Surface Supérieure (Toit)', indices: [4, 5, 6, 7], shade: 1.15, dimText: `${presentation.format(l, unit, 'cargoLength', false)} × ${presentation.format(w, unit, 'cargoLength', false)} ${presentation.unit('cargoLength')}` },
        { id: 'front', name: 'Face Avant (L × H)', indices: [0, 3, 7, 4], shade: 0.95, dimText: `${presentation.format(l, unit, 'cargoLength', false)} × ${presentation.format(h, unit, 'cargoLength', false)} ${presentation.unit('cargoLength')}` },
        { id: 'right', name: 'Face Latérale Droite (l × H)', indices: [3, 2, 6, 7], shade: 0.8, dimText: `${presentation.format(w, unit, 'cargoLength', false)} × ${presentation.format(h, unit, 'cargoLength', false)} ${presentation.unit('cargoLength')}` },
        { id: 'back', name: 'Face Arrière (L × H)', indices: [2, 1, 5, 6], shade: 0.65, dimText: `${presentation.format(l, unit, 'cargoLength', false)} × ${presentation.format(h, unit, 'cargoLength', false)} ${presentation.unit('cargoLength')}` },
        { id: 'left', name: 'Face Latérale Gauche (l × H)', indices: [1, 0, 4, 5], shade: 0.72, dimText: `${presentation.format(w, unit, 'cargoLength', false)} × ${presentation.format(h, unit, 'cargoLength', false)} ${presentation.unit('cargoLength')}` },
    ]

    const sortedFaces = useMemo(() => {
        return faces.map(f => {
            const avgDepth = f.indices.reduce((sum, idx) => sum + pVerts[idx][2], 0) / f.indices.length
            return { ...f, avgDepth }
        }).sort((a, b) => a.avgDepth - b.avgDepth)
    }, [pVerts])

    // ── Axes Arrows Geometry ────────────────────────────────────────────────────
    const axisExt = maxDim * 0.4
    const zAxisExt = maxDim * 0.6 // Z axis longer for better visibility
    // Origin corner (0,0,0) is at vertex 0: (halfL, halfW, 0) - Top Right (displayed as top-left)
    const oPt = pVerts[0]

    // X Axis (Longitudinal / Red) along X (from halfL to -halfL - extension) - pointing left
    const pXEnd = project(-halfL - axisExt, halfW, 0)
    // Y Axis (Lateral / Green) along Y (from halfW to -halfW - extension) - pointing down
    const pYEnd = project(halfL, -halfW - axisExt, 0)
    // Z Axis (Vertical / Blue) along Z (from 0 to h + extension) - pointing up
    const pZEnd = project(halfL, halfW, h + zAxisExt)

    // Edge dimension arrow points
    const pEdgeLStart = project(halfL, halfW + maxDim * 0.1, 0)
    const pEdgeLEnd = project(-halfL, halfW + maxDim * 0.1, 0)

    const pEdgeWStart = project(halfL + maxDim * 0.1, halfW, 0)
    const pEdgeWEnd = project(halfL + maxDim * 0.1, -halfW, 0)

    const pEdgeHStart = project(halfL + maxDim * 0.1, halfW, 0)
    const pEdgeHEnd = project(halfL + maxDim * 0.1, halfW, h)

    // ── Safety clamp against upstream data anomalies ────────────────────────────
    // If an unexpected value ever reaches this component (e.g. a unit-conversion
    // bug somewhere upstream feeding a wildly wrong height/length/width into the
    // projection), a single axis endpoint can land thousands of pixels away from
    // the rest of the scene. Since the ViewBox auto-fits to every drawn point,
    // that one bad point would blow up the ViewBox and shrink the entire 3D
    // preview down to an invisible speck. To make the component resilient to
    // that regardless of root cause, we clamp every axis endpoint (in screen
    // space) to a sane maximum distance from the origin point, based on the
    // box's own on-screen diagonal — this never affects normal-looking scenes,
    // it only kicks in when something is actually wrong upstream.
    const clampToBox = (pt: [number, number, number]): [number, number, number] => {
        const boxXs = pVerts.map(v => v[0])
        const boxYs = pVerts.map(v => v[1])
        const boxDiag = Math.hypot(Math.max(...boxXs) - Math.min(...boxXs), Math.max(...boxYs) - Math.min(...boxYs)) || 1
        const maxReach = boxDiag * 2.2 // generous room for axis extensions + labels
        const dx = pt[0] - oPt[0]
        const dy = pt[1] - oPt[1]
        const dist = Math.hypot(dx, dy)
        if (dist <= maxReach || dist === 0) return pt
        const k = maxReach / dist
        return [oPt[0] + dx * k, oPt[1] + dy * k, pt[2]]
    }
    const pXEndSafe = clampToBox(pXEnd)
    const pYEndSafe = clampToBox(pYEnd)
    const pZEndSafe = clampToBox(pZEnd)
    const axisLabels = {
        x: `X (L: ${presentation.format(l, unit, 'cargoLength')})`,
        y: `Y (W: ${presentation.format(w, unit, 'cargoLength')})`,
        z: `Z (H: ${presentation.format(h, unit, 'cargoLength')})`,
    }
    // Le repère métier part du sommet 0 ; X et Y avancent vers les faces opposées.
    const cgProjection = cgLocal ? {
        point: project(halfL - cgLocal.x, halfW - cgLocal.y, cgLocal.z),
        floor: project(halfL - cgLocal.x, halfW - cgLocal.y, 0),
        x: project(halfL - cgLocal.x, halfW, 0),
        y: project(halfL, halfW - cgLocal.y, 0),
        z: project(halfL, halfW, cgLocal.z),
    } : null
    const floorCenter = project(0, 0, 0)
    const cgAxisMarkers = cgProjection && cg.pointMm ? ([
        { axis: 'x', point: cgProjection.x, end: pXEndSafe, color: xAxisColor, value: cg.pointMm.x },
        { axis: 'y', point: cgProjection.y, end: pYEndSafe, color: isDark ? '#34d399' : '#047857', value: cg.pointMm.y },
        { axis: 'z', point: cgProjection.z, end: pZEndSafe, color: isDark ? '#60a5fa' : '#2563eb', value: cg.pointMm.z },
    ] as const).map(marker => {
        const dx = marker.end[0] - oPt[0], dy = marker.end[1] - oPt[1]
        const magnitude = Math.hypot(dx, dy) || 1
        let nx = -dy / magnitude, ny = dx / magnitude
        // Put floor labels outside the footprint; keep Z labels left of the vertical axis.
        if (marker.axis === 'z') { nx = -1; ny = 0 }
        else if (nx * (floorCenter[0] - oPt[0]) + ny * (floorCenter[1] - oPt[1]) > 0) { nx = -nx; ny = -ny }
        return { ...marker, nx, ny, label: `${marker.axis} = ${presentation.format(marker.value, 'mm', 'cargoLength')}` }
    }) : []

    // ── Dynamic ViewBox (prevents any part of the scene from being cropped) ────
    // The nominal svgWidth x svgHeight box is just a baseline. Depending on yaw/pitch
    // and the product's aspect ratio, axis tips (especially the Z axis, whose label
    // sits well above the box) can land outside that nominal box. Instead of guessing
    // a fixed safety margin, we compute the real bounding box of every point we draw
    // (vertices, axis tips, dimension guides) and fit the viewBox to it, with padding
    // reserved for the axis labels/circles. This guarantees nothing is ever truncated,
    // regardless of camera angle or product proportions.
    const viewBox = useMemo(() => {
        const pts: [number, number][] = [
            ...pVerts.map(v => [v[0], v[1]] as [number, number]),
            [pXEndSafe[0], pXEndSafe[1]], [pYEndSafe[0], pYEndSafe[1]], [pZEndSafe[0], pZEndSafe[1]],
            [pEdgeLStart[0], pEdgeLStart[1]], [pEdgeLEnd[0], pEdgeLEnd[1]],
            [pEdgeWStart[0], pEdgeWStart[1]], [pEdgeWEnd[0], pEdgeWEnd[1]],
            [pEdgeHStart[0], pEdgeHStart[1]], [pEdgeHEnd[0], pEdgeHEnd[1]],
            // Reserve the full label width, including on narrow mobile viewports.
            [pXEndSafe[0] + (pXEndSafe[0] >= oPt[0] ? 10 : -15) + axisLabels.x.length * 8, pXEndSafe[1]],
            [pYEndSafe[0] + (pYEndSafe[0] >= oPt[0] ? 10 : -15) + axisLabels.y.length * 8, pYEndSafe[1]],
            [pZEndSafe[0] + 8 + axisLabels.z.length * 8, pZEndSafe[1] - 6],
        ]
        const xs = pts.map(p => p[0])
        const ys = pts.map(p => p[1])
        // Extra padding for label text, glow blur, and end-of-axis circles
        const padX = 55
        const padY = 30
        const minX = Math.min(...xs) - padX
        const maxX = Math.max(...xs) + padX
        const minY = Math.min(...ys) - padY
        const maxY = Math.max(...ys) + padY
        return { minX, minY, w: maxX - minX, h: maxY - minY }
    }, [pVerts, pXEndSafe, pYEndSafe, pZEndSafe, pEdgeLStart, pEdgeLEnd, pEdgeWStart, pEdgeWEnd, pEdgeHStart, pEdgeHEnd])

    // Modifier le cadrage après l'ajustement automatique pour que le zoom reste effectif.
    const zoomedViewBox = {
        x: viewBox.minX + (viewBox.w - viewBox.w / zoom) / 2 - pan.x,
        y: viewBox.minY + (viewBox.h - viewBox.h / zoom) / 2 - pan.y,
        width: viewBox.w / zoom,
        height: viewBox.h / zoom,
    }

    return (
        <div role="region" aria-label="Aperçu 3D du produit" className={containerClass}>
            {appearance === 'light' && <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-4 py-4">
                <h3 className="inline-flex items-center gap-2 text-sm font-semibold text-slate-900"><Box size={17} className="text-orange-500" />Aperçu 3D</h3>
                <span className="rounded-full bg-orange-50 px-2 py-1 text-[10px] font-medium text-orange-600">Interactif</span>
            </div>}
            <div className="flex items-center justify-between gap-2 overflow-x-auto border-b px-3 py-2.5" style={{ borderColor: isDark ? '#334155' : '#e2e8f0' }}>
                <span className={`min-w-0 flex-1 truncate text-xs ${hintTextColor}`} title="Glisser : clic gauche pour orienter · clic droit pour déplacer · roulette pour zoomer">Clic gauche : rotation · clic droit : déplacement · roulette : zoom</span>
                <div role="group" aria-label="Commandes de la vue 3D" className="flex shrink-0 items-center gap-1">
                    <button type="button" title="Zoom arrière" aria-label="Zoom arrière" disabled={zoom <= 0.5} onClick={() => setZoom(value => Math.max(0.5, value - 0.25))} className={resetButtonClass}><ZoomOut size={16} /></button>
                    <output aria-label="Niveau de zoom" aria-live="polite" className={`w-10 shrink-0 whitespace-nowrap text-center text-xs font-mono ${hintTextColor}`}>{Math.round(zoom * 100)} %</output>
                    <button type="button" title="Zoom avant" aria-label="Zoom avant" disabled={zoom >= 2.5} onClick={() => setZoom(value => Math.min(2.5, value + 0.25))} className={resetButtonClass}><ZoomIn size={16} /></button>
                    <button type="button" title="Auto-rotation 360°" aria-pressed={autoRotate} onClick={() => setAutoRotate(value => !value)} className={`min-h-10 w-20 shrink-0 whitespace-nowrap rounded-lg border px-2 text-xs font-semibold transition-colors ${autoRotateButtonClass}`}>{autoRotate ? '⏸ Pause' : '🔄 Rotation'}</button>
                    <button type="button" title="Réinitialiser la vue" aria-label="Réinitialiser la vue" onClick={() => {
                        dragCleanupRef.current?.()
                        setYaw(225); setPitch(28); setZoom(1); setPan({ x: 0, y: 0 }); setSelectedFace(null); setAutoRotate(false)
                    }} className={resetButtonClass}><RotateCcw size={16} /></button>
                </div>
            </div>
            {/* Main Canvas Area - Full Width */}
            <div className="relative">

                {/* Interactive 3D SVG Viewport - Full Width */}
                <div
                    ref={viewportRef}
                    onPointerDown={handlePointerDown}
                    onContextMenu={event => event.preventDefault()}
                    className={canvasContainerClass}
                >
                    {/* Ambient Radial Gradient Background */}
                    <div
                        className="absolute inset-0 opacity-20 pointer-events-none"
                        style={{
                            backgroundImage: 'radial-gradient(#94a3b8 1px, transparent 1px)',
                            backgroundSize: '20px 20px',
                        }}
                    />

                    {/* 3D SVG Canvas */}
                    <svg
                        ref={svgRef}
                        width={svgWidth}
                        height={svgHeight}
                        viewBox={`${zoomedViewBox.x} ${zoomedViewBox.y} ${zoomedViewBox.width} ${zoomedViewBox.height}`}
                        preserveAspectRatio="xMidYMid meet"
                        className="z-0"
                        style={{ width: '100%', height: '100%' }}
                        role="img" aria-label="Dimensions du produit en trois dimensions"
                    >
                        <defs>
                            <filter id="glow-x" filterUnits="userSpaceOnUse" x={-200} y={-300} width={svgWidth + 400} height={svgHeight + 600}>
                                <feGaussianBlur stdDeviation="3" result="blur" />
                                <feComposite in="SourceGraphic" in2="blur" operator="over" />
                            </filter>
                            <filter id="glow-y" filterUnits="userSpaceOnUse" x={-200} y={-300} width={svgWidth + 400} height={svgHeight + 600}>
                                <feGaussianBlur stdDeviation="3" result="blur" />
                                <feComposite in="SourceGraphic" in2="blur" operator="over" />
                            </filter>
                            {/* glow-z: filterUnits="userSpaceOnUse" with a fixed region is REQUIRED here.
                                The Z axis segment goes from (halfL, halfW, 0) to (halfL, halfW, h+zAxisExt):
                                same x/y at both ends, so its projected sx is constant for any yaw/pitch —
                                it is always a perfectly vertical screen line with a ZERO-WIDTH bounding box.
                                With the default objectBoundingBox percentage region (e.g. width="160%"),
                                160% of 0 is still 0, so browsers collapse the filter region to nothing and
                                the whole filtered line (including the source graphic) disappears. */}
                            <filter id="glow-z" filterUnits="userSpaceOnUse" x={-200} y={-300} width={svgWidth + 400} height={svgHeight + 600}>
                                <feGaussianBlur stdDeviation="3" result="blur" />
                                <feComposite in="SourceGraphic" in2="blur" operator="over" />
                            </filter>
                            {/* Pattern ground grid */}
                            <pattern id="ground-grid" width="20" height="20" patternUnits="userSpaceOnUse">
                                <path d="M 20 0 L 0 0 0 20" fill="none" stroke={gridStroke} strokeWidth="1" />
                            </pattern>
                        </defs>

                        {/* Ground Footprint Grid Surface (Z=0 Plane Overlay) */}
                        <g id="ground-footprint">
                            <polygon
                                points={`${pVerts[0][0]},${pVerts[0][1]} ${pVerts[1][0]},${pVerts[1][1]} ${pVerts[2][0]},${pVerts[2][1]} ${pVerts[3][0]},${pVerts[3][1]}`}
                                fill="url(#ground-grid)"
                                stroke={gridLineStroke}
                                strokeWidth="1.5"
                                strokeDasharray="4 2"
                                opacity={0.8}
                            />
                        </g>

                        {/* Render 3D Box Faces (Sorted by depth) */}
                        {sortedFaces.map(f => {
                            const pts = f.indices.map(idx => `${pVerts[idx][0].toFixed(1)},${pVerts[idx][1].toFixed(1)}`).join(' ')
                            const isSelected = selectedFace === f.id
                            const faceColor = isSelected ? '#f59e0b' : adjustBrightness(color, f.shade)

                            return (
                                <polygon
                                    key={f.id}
                                    points={pts}
                                    fill={faceColor}
                                    stroke={isSelected ? (isDark ? '#ffffff' : '#000000') : faceStroke}
                                    strokeWidth={isSelected ? '2.5' : '1.2'}
                                    strokeLinejoin="round"
                                    opacity={isSelected ? 0.95 : 0.88}
                                    className="transition-all duration-150 cursor-pointer hover:opacity-100"
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        setSelectedFace(f.id === selectedFace ? null : f.id)
                                    }}
                                />
                            )
                        })}

                        {/* ── 3D AXES (X, Y, Z) WITH ARROWS & LABELS ── */}
                        {/* AXE X (Rouge - Longueur L) */}
                        <g id="axis-x">
                            <line
                                x1={oPt[0]} y1={oPt[1]}
                                x2={pXEndSafe[0]} y2={pXEndSafe[1]}
                                stroke={xAxisColor}
                                strokeWidth="2.5"
                                filter="url(#glow-x)"
                            />
                            <circle cx={pXEndSafe[0]} cy={pXEndSafe[1]} r="4" fill={xAxisColor} />
                            <text
                                x={pXEndSafe[0] + (pXEndSafe[0] >= oPt[0] ? 10 : -15)}
                                y={pXEndSafe[1] + (pXEndSafe[1] >= oPt[1] ? 12 : -5)}
                                fill={xAxisColor}
                                fontSize="11"
                                fontWeight="900"
                                fontFamily="mono"
                            >
                                {axisLabels.x}
                            </text>
                        </g>

                        {/* AXE Y (Vert - Largeur W) */}
                        <g id="axis-y">
                            <line
                                x1={oPt[0]} y1={oPt[1]}
                                x2={pYEndSafe[0]} y2={pYEndSafe[1]}
                                stroke={isDark ? "#10b981" : "#059669"}
                                strokeWidth="2.5"
                                filter="url(#glow-y)"
                            />
                            <circle cx={pYEndSafe[0]} cy={pYEndSafe[1]} r="4" fill={isDark ? "#10b981" : "#059669"} />
                            <text
                                x={pYEndSafe[0] + (pYEndSafe[0] >= oPt[0] ? 10 : -15)}
                                y={pYEndSafe[1] + (pYEndSafe[1] >= oPt[1] ? 12 : -5)}
                                fill={isDark ? "#10b981" : "#059669"}
                                fontSize="11"
                                fontWeight="900"
                                fontFamily="mono"
                            >
                                {axisLabels.y}
                            </text>
                        </g>

                        {/* AXE Z (Bleu - Hauteur H) - Rendered after faces to be visible */}
                        <g id="axis-z">
                            <line
                                x1={oPt[0]} y1={oPt[1]}
                                x2={pZEndSafe[0]} y2={pZEndSafe[1]}
                                stroke={isDark ? "#3b82f6" : "#2563eb"}
                                strokeWidth="3"
                                strokeLinecap="round"
                                filter="url(#glow-z)"
                            />
                            <circle cx={pZEndSafe[0]} cy={pZEndSafe[1]} r="6" fill={isDark ? "#3b82f6" : "#2563eb"} />
                            <text
                                x={pZEndSafe[0] + 8}
                                y={pZEndSafe[1] - 6}
                                fill={isDark ? "#3b82f6" : "#2563eb"}
                                fontSize="12"
                                fontWeight="900"
                                fontFamily="mono"
                            >
                                {axisLabels.z}
                            </text>
                        </g>

                        {/* Origin Node (0, 0, 0) */}
                        <circle cx={oPt[0]} cy={oPt[1]} r="5" fill={isDark ? "#ffffff" : "#000000"} stroke={isDark ? "#0f172a" : "#ffffff"} strokeWidth="2" />

                        {/* Dimension Extension Line Helpers */}
                        {/* L dimension guide */}
                        <line x1={pEdgeLStart[0]} y1={pEdgeLStart[1]} x2={pEdgeLEnd[0]} y2={pEdgeLEnd[1]} stroke={xAxisColor} strokeWidth="1" strokeDasharray="2 2" opacity={0.6} />
                        {/* W dimension guide */}
                        <line x1={pEdgeWStart[0]} y1={pEdgeWStart[1]} x2={pEdgeWEnd[0]} y2={pEdgeWEnd[1]} stroke={isDark ? "#10b981" : "#059669"} strokeWidth="1" strokeDasharray="2 2" opacity={0.6} />
                        {/* H dimension guide */}
                        <line x1={pEdgeHStart[0]} y1={pEdgeHStart[1]} x2={pEdgeHEnd[0]} y2={pEdgeHEnd[1]} stroke={isDark ? "#3b82f6" : "#2563eb"} strokeWidth="1" strokeDasharray="2 2" opacity={0.6} />
                        {cgProjection && <g role="img" aria-label={`Centre de gravité : ${cgCoordinates}`} pointerEvents="none">
                            <title>Centre de gravité — {cgCoordinates}</title>
                            <line x1={oPt[0]} y1={oPt[1]} x2={cgProjection.x[0]} y2={cgProjection.x[1]} stroke={xAxisColor} strokeWidth={1.5} strokeDasharray="4 3" />
                            <line x1={cgProjection.x[0]} y1={cgProjection.x[1]} x2={cgProjection.floor[0]} y2={cgProjection.floor[1]} stroke="#10b981" strokeWidth={1.5} strokeDasharray="4 3" />
                            <line x1={cgProjection.floor[0]} y1={cgProjection.floor[1]} x2={cgProjection.y[0]} y2={cgProjection.y[1]} stroke={xAxisColor} strokeWidth={1} strokeDasharray="3 3" opacity={0.7} />
                            <line x1={oPt[0]} y1={oPt[1]} x2={cgProjection.y[0]} y2={cgProjection.y[1]} stroke="#10b981" strokeWidth={1.5} strokeDasharray="4 3" />
                            <line x1={cgProjection.floor[0]} y1={cgProjection.floor[1]} x2={cgProjection.point[0]} y2={cgProjection.point[1]} stroke="#3b82f6" strokeWidth={1.5} strokeDasharray="4 3" />
                            <line x1={cgProjection.point[0]} y1={cgProjection.point[1]} x2={cgProjection.z[0]} y2={cgProjection.z[1]} stroke="#3b82f6" strokeWidth={1} strokeDasharray="3 3" opacity={0.6} />
                            {cgAxisMarkers.map(marker => <g key={marker.axis} role="group" aria-label={`Projection sur ${marker.axis.toUpperCase()} : ${marker.label}`}>
                                <line x1={marker.point[0] - marker.nx * 3} y1={marker.point[1] - marker.ny * 3}
                                    x2={marker.point[0] + marker.nx * 4} y2={marker.point[1] + marker.ny * 4} stroke={marker.color} strokeWidth={1.2} />
                                <text x={marker.point[0] + marker.nx * 10} y={marker.point[1] + marker.ny * 10}
                                    textAnchor={marker.nx < -0.15 ? 'end' : marker.nx > 0.15 ? 'start' : 'middle'} dominantBaseline="middle"
                                    fontSize={9} fontWeight={500} fontFamily="ui-monospace, monospace" fill={marker.color}
                                    stroke={isDark ? '#0f172a' : '#f8fafc'} strokeWidth={3} strokeLinejoin="round" paintOrder="stroke">{marker.label}</text>
                            </g>)}
                            <circle cx={cgProjection.point[0]} cy={cgProjection.point[1]} r={10} fill={isDark ? '#0f172a' : '#fffbeb'} opacity={0.9} />
                            <Crosshair x={cgProjection.point[0] - 10} y={cgProjection.point[1] - 10} size={20} color="#f97316" strokeWidth={2} />
                        </g>}
                    </svg>

                    {/* Current Selected Face Badge Overlay */}
                    {selectedFace && (
                        <div className={`absolute bottom-2.5 left-3 px-3 py-1.5 rounded-xl bg-amber-500/20 border border-amber-500/40 text-amber-600 dark:text-amber-300 text-xs font-semibold backdrop-blur-md`}>
                            Face sélectionnée : <strong>{faces.find(f => f.id === selectedFace)?.name}</strong> ({faces.find(f => f.id === selectedFace)?.dimText})
                        </div>
                    )}

                    {/* Volume Display Overlay - Bottom Right */}
                    {calculatedVolume && (
                        <div className="absolute bottom-2.5 right-3 rounded-xl border border-slate-200 bg-white/90 px-3 py-1.5 text-xs font-medium text-slate-600 backdrop-blur-md">
                            Volume calculé : <strong><Measure value={calculatedVolume} from="m3" quantity="volume" /></strong>
                        </div>
                    )}
                </div>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t px-4 py-3 text-xs" aria-live="polite" style={{ borderColor: isDark ? '#334155' : '#e2e8f0', color: isDark ? '#cbd5e1' : '#475569' }}>
                <span className="inline-flex items-center gap-1.5 font-semibold"><Crosshair size={15} className="text-orange-500" />Centre de gravité</span>
                {cg.pointMm ? <><span className="font-mono">{cgCoordinates}</span><span className="rounded-full px-2 py-0.5" style={{ background: isDark ? '#292014' : '#fff7ed', color: isDark ? '#fdba74' : '#ea580c' }}>{cg.geometric ? 'Centre géométrique' : 'Personnalisé'}</span></>
                    : <span>{cg.status === 'missing-dimensions' ? 'Complétez les dimensions pour afficher sa position.' : 'Les coordonnées doivent se situer à l’intérieur du produit.'}</span>}
            </div>
        </div>
    )
}
